import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Copy, CheckCircle, ArrowRight, PencilSimple } from '@phosphor-icons/react';

/**
 * Round 57 — Today's Mission as the *primary execution surface*.
 * Round 105 (2026-02-14) — the panel now treats the payload as an
 * EDITABLE DRAFT: every string / array field is rendered inside an
 * inline textarea so the customer can tweak Hook, Caption, Script,
 * Hashtags, CTA, Subject, Body, Headline, etc. Edits are held in
 * local state and streamed up to the parent via `onEditsChange`
 * (delta over the seed payload). Parent persists via
 * `POST /api/todays-mission-v2/{asset_id}/save-edits` when
 * "Approve & Generate" is pressed.
 *
 * Visual layout is unchanged; the only new affordance is that
 * the value nodes are now `<textarea>` / `<input>` with the same
 * text-white styling and no visible chrome until focused.
 */
const ExecutionPanel = ({
  tier,
  tierNote,
  mission,
  assetPayload,
  todayProgress,
  businessName,
  publishing,
  onMarkPublished,
  editable = true,
  pendingEdits,
  onEditsChange,
}) => {
  const kind = assetPayload?.kind || mission?.asset_type || 'asset';

  // Sections we may render per kind — order matters (most useful
  // first so the user can copy + ship top-down).
  const SECTIONS = useMemo(() => ({
    reel: ['hook', 'voice_script', 'video_script', 'script', 'caption', 'hashtags', 'cta', 'video_prompt', 'voice_prompt', 'platform'],
    post: ['hook', 'caption', 'hashtags', 'cta', 'video_prompt', 'platform'],
    content_pack: ['hook', 'voice_script', 'video_script', 'script', 'caption', 'hashtags', 'cta', 'video_prompt', 'voice_prompt', 'platform'],
    lead_magnet: ['subheadline', 'body', 'suggested_social_post', 'cta_copy', 'recommended_platform'],
    email: ['subject', 'full_message', 'cta', 'recommended_audience'],
    offer: ['pitch', 'price', 'cta', 'where_to_pitch'],
    sales_page: ['headline', 'subheadline', 'body', 'cta', 'link'],
    business_profile: ['summary', 'key_points'],
  }), []);

  const sections = (SECTIONS[kind] || ['summary']).filter(
    (key) => {
      // Round 109 — always render the CANONICAL fields for this
      // asset kind so the customer can EDIT empty drafts (e.g. a
      // legacy DFY-video mission arrives with only a title; the
      // customer must still be able to type a hook / caption /
      // voice-script / video-script / video-prompt in place).
      // We keep the special-cases for the "post" kind minimal:
      // for reel/content_pack always render the core fields even
      // when empty; for other kinds keep the presence check.
      const seed = assetPayload?.[key];
      const edited = (pendingEdits || {})[key];
      const v = edited !== undefined ? edited : seed;
      const alwaysShow = new Set([
        'hook', 'caption', 'cta', 'hashtags', 'platform',
        'voice_script', 'video_script', 'video_prompt',
      ]);
      if ((kind === 'reel' || kind === 'content_pack' || kind === 'post')
          && alwaysShow.has(key)) {
        return true;
      }
      return Array.isArray(v) ? v.length > 0 : Boolean(v);
    },
  );

  // Local text buffers keyed by section — hashtags/key_points are
  // rendered as space-separated strings for a friendlier edit UX.
  const seedValue = useCallback((key) => {
    const raw = assetPayload?.[key];
    if (Array.isArray(raw)) return raw.join(' ');
    return raw || '';
  }, [assetPayload]);

  const [drafts, setDrafts] = useState(() => {
    const initial = {};
    sections.forEach((k) => { initial[k] = seedValue(k); });
    return initial;
  });

  // Rehydrate the drafts whenever the seed payload changes
  // (fresh mission fetched, previous mission finalized, etc.).
  useEffect(() => {
    const next = {};
    (SECTIONS[kind] || ['summary']).forEach((k) => {
      const seed = seedValue(k);
      const edited = (pendingEdits || {})[k];
      next[k] = edited !== undefined ? (
        Array.isArray(edited) ? edited.join(' ') : String(edited)
      ) : seed;
    });
    setDrafts(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetPayload, kind]);

  const updateField = (key, text) => {
    setDrafts((prev) => ({ ...prev, [key]: text }));
    if (!onEditsChange) return;
    const seed = assetPayload?.[key];
    const isArrayField = key === 'hashtags' || key === 'key_points';
    const parsed = isArrayField
      ? text.split(/\s+/).filter(Boolean)
      : text;
    // Only surface fields that actually diverge from the seed
    // so we send a clean delta upstream.
    const seedStr = Array.isArray(seed) ? seed.join(' ') : (seed || '');
    const nextDelta = { ...(pendingEdits || {}) };
    if (text === seedStr) {
      delete nextDelta[key];
    } else {
      nextDelta[key] = parsed;
    }
    onEditsChange(nextDelta);
  };

  const copy = async (rawText, label) => {
    if (!rawText) return;
    try {
      await navigator.clipboard.writeText(
        Array.isArray(rawText) ? rawText.join(' ') : rawText,
      );
      toast.success(`${label} copied to clipboard.`);
    } catch (err) {
      console.error('Clipboard failed:', err);
      toast.error('Could not copy — please select and copy manually.');
    }
  };

  const copyAll = async () => {
    const parts = [];
    if (assetPayload.title) parts.push(assetPayload.title);
    sections.forEach((key) => {
      const v = drafts[key];
      if (!v) return;
      parts.push(v);
    });
    if (!parts.length) return;
    try {
      await navigator.clipboard.writeText(parts.join('\n\n'));
      toast.success('Full asset copied to clipboard.');
    } catch (err) {
      console.error('Clipboard failed:', err);
      toast.error('Could not copy — please select and copy manually.');
    }
  };

  const LABELS = {
    hook: 'Hook',
    voice_script: 'Voice script',
    video_script: 'Video script',
    script: 'Script',
    caption: 'Caption',
    hashtags: 'Hashtags',
    cta: 'Call to action',
    cta_copy: 'CTA copy',
    video_prompt: 'Video prompt',
    voice_prompt: 'Voice prompt',
    platform: 'Platform',
    subheadline: 'Subheadline',
    body: 'Full text',
    suggested_social_post: 'Suggested social post',
    recommended_platform: 'Recommended platform',
    subject: 'Subject line',
    full_message: 'Full message',
    recommended_audience: 'Recommended audience',
    pitch: 'Pitch',
    price: 'Price',
    where_to_pitch: 'Where to pitch',
    headline: 'Headline',
    link: 'Link',
    summary: 'Summary',
    key_points: 'Key points',
  };

  const progressPct = todayProgress?.total
    ? Math.min(100, Math.round((todayProgress.done / todayProgress.total) * 100))
    : 0;

  const isEdited = pendingEdits && Object.keys(pendingEdits).length > 0;

  // Guard AFTER hooks are declared so hook order stays stable
  // across renders even when the payload briefly disappears
  // during a refetch.
  if (!assetPayload) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-transparent to-orange-500/10 p-4 sm:p-5 mb-4"
      data-testid="execution-panel"
    >
      {/* Header — tier, business pill, today's progress */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className="text-[10px] uppercase tracking-wider text-amber-300 font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30"
            data-testid="execution-panel-kind"
          >
            {String(kind).replace(/_/g, ' ')}
          </span>
          {editable && (
            <span
              className="text-[10px] uppercase tracking-wider text-amber-200 font-semibold px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/25 inline-flex items-center gap-1"
              data-testid="execution-panel-draft-pill"
              title="This is a draft — edit any field, then press Approve & Generate."
            >
              <PencilSimple size={10} weight="bold" /> Draft
            </span>
          )}
          {isEdited && (
            <span
              className="text-[10px] uppercase tracking-wider text-emerald-300 font-semibold px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30"
              data-testid="execution-panel-edited-pill"
            >
              Edited
            </span>
          )}
          {tier && (
            <span
              className="text-[10px] uppercase tracking-wider text-slate-300 px-2 py-0.5 rounded-full bg-white/5 border border-white/10"
              data-testid="execution-panel-tier"
            >
              {tier} tier
            </span>
          )}
          {businessName && (
            <span
              className="text-xs text-white/80 truncate max-w-[220px]"
              data-testid="execution-panel-business"
            >
              · {businessName}
            </span>
          )}
        </div>
        {todayProgress?.total > 0 && (
          <div className="text-right" data-testid="execution-panel-progress">
            <p className="text-[10px] uppercase tracking-wider text-slate-400">Today</p>
            <p className="text-sm font-bold text-white">
              {todayProgress.done} of {todayProgress.total} done
            </p>
            <div className="w-24 h-1.5 bg-white/10 rounded-full mt-1 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-amber-500 to-orange-500"
                   style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* Title */}
      <h3
        className="text-lg sm:text-xl font-bold text-white mb-1 leading-tight"
        data-testid="execution-panel-title"
      >
        {assetPayload.title || mission?.title || "Today's mission"}
      </h3>
      {assetPayload.scheduled_day && (
        <p className="text-xs text-slate-400 mb-3" data-testid="execution-panel-schedule">
          {assetPayload.scheduled_day}
          {assetPayload.scheduled_date ? ` · ${assetPayload.scheduled_date}` : ''}
        </p>
      )}
      {tierNote && (
        <p className="text-xs text-amber-300/80 mb-3" data-testid="execution-panel-tier-note">
          {tierNote}
        </p>
      )}

      {/* Asset sections */}
      <div className="space-y-3" data-testid="execution-panel-sections">
        {sections.map((key) => {
          const label = LABELS[key] || key;
          const isHashtags = key === 'hashtags' || key === 'key_points';
          const isShort = key === 'platform' || key === 'price' || key === 'recommended_platform'
            || key === 'recommended_audience' || key === 'link' || key === 'cta' || key === 'cta_copy'
            || key === 'where_to_pitch' || key === 'subject';
          const draftValue = drafts[key] ?? '';
          const rows = isShort || isHashtags
            ? 1
            : Math.min(10, Math.max(2, Math.ceil((draftValue.length || 0) / 80)));
          return (
            <div
              key={key}
              className="rounded-lg border border-white/10 bg-white/5 p-3"
              data-testid={`execution-panel-field-${key}`}
            >
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
                <button
                  onClick={() => copy(draftValue, label)}
                  className="text-[11px] text-slate-300 hover:text-white inline-flex items-center gap-1"
                  data-testid={`execution-panel-copy-${key}`}
                >
                  <Copy size={12} /> Copy
                </button>
              </div>
              {editable ? (
                isShort ? (
                  <input
                    type="text"
                    value={draftValue}
                    onChange={(e) => updateField(key, e.target.value)}
                    className="w-full text-sm text-white bg-transparent border-none outline-none focus:ring-1 focus:ring-amber-400/60 focus:bg-white/5 rounded px-1 py-0.5 placeholder-slate-500"
                    placeholder={`Add ${label.toLowerCase()}…`}
                    data-testid={`execution-panel-input-${key}`}
                  />
                ) : (
                  <textarea
                    value={draftValue}
                    onChange={(e) => updateField(key, e.target.value)}
                    rows={rows}
                    className={`w-full text-sm text-white bg-transparent border-none outline-none resize-y focus:ring-1 focus:ring-amber-400/60 focus:bg-white/5 rounded px-1 py-0.5 placeholder-slate-500 ${isHashtags ? 'break-words' : 'whitespace-pre-wrap leading-relaxed'}`}
                    placeholder={`Add ${label.toLowerCase()}…`}
                    data-testid={`execution-panel-input-${key}`}
                  />
                )
              ) : (
                isHashtags ? (
                  <p className="text-sm text-white break-words">{draftValue}</p>
                ) : isShort ? (
                  <p className="text-sm text-white">{draftValue}</p>
                ) : (
                  <p className="text-sm text-white whitespace-pre-wrap leading-relaxed">{draftValue}</p>
                )
              )}
            </div>
          );
        })}

        {assetPayload.video_url && (
          <div
            className="rounded-lg border border-violet-500/30 bg-violet-500/10 p-3"
            data-testid="execution-panel-video"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-wider text-violet-300 font-semibold">
                Your finished video
              </p>
              <a
                href={assetPayload.video_url}
                target="_blank"
                rel="noreferrer"
                className="text-[11px] text-violet-200 hover:text-white inline-flex items-center gap-1"
                data-testid="execution-panel-video-open"
              >
                Open <ArrowRight size={12} />
              </a>
            </div>
            <video
              src={assetPayload.video_url}
              controls
              className="w-full rounded-md max-h-80 bg-black"
              data-testid="execution-panel-video-player"
            />
          </div>
        )}

        {assetPayload.publishing_instructions && (
          <div
            className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3"
            data-testid="execution-panel-instructions"
          >
            <p className="text-[10px] uppercase tracking-wider text-emerald-300 font-semibold mb-1">
              How to publish
            </p>
            <p className="text-sm text-white whitespace-pre-wrap leading-relaxed">
              {assetPayload.publishing_instructions}
            </p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-2 mt-4">
        <button
          onClick={copyAll}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 text-white text-sm font-semibold transition-colors"
          data-testid="execution-panel-copy-all"
        >
          <Copy size={16} /> Copy full asset
        </button>
        <button
          onClick={onMarkPublished}
          disabled={publishing}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white text-sm font-semibold transition-colors disabled:opacity-60"
          data-testid="execution-panel-mark-published"
        >
          {publishing ? 'Marking…' : (
            <>
              <CheckCircle size={16} weight="fill" /> Mark as {actionVerb(kind)} <ArrowRight size={14} />
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
};

function actionVerb(kind) {
  switch (kind) {
    case 'email':       return 'sent';
    case 'offer':       return 'pitched';
    case 'lead_magnet': return 'shared';
    case 'sales_page':  return 'published';
    case 'business_profile': return 'reviewed';
    default:            return 'posted';
  }
}

export default ExecutionPanel;
